/* ═══════════════════════════════════════════════════════════════════════════
   Identité SONORE des armes — la couche présentation, pas la simulation.

   Le son d'une arme est une décision de PRÉSENTATION : la simulation, pure, n'a
   aucune raison de connaître un identifiant de bruitage. On garde donc le lien
   arme → son ICI, indexé par l'identifiant d'arme (stable, contrairement à un
   index de fichier). Une arme absente de la table retombe sur le son générique.

   Trois tables :
     · WEAPON_SOUND    — le tir primaire, un timbre par canon
     · SECONDARY_SOUND — le DÉCLENCHEMENT de la secondaire
     · SECONDARY_LOOP  — la BOUCLE tant qu'un projectile de l'arme est en vol
       (missile qui file, rayon qui dure, scie qui tourne). Toujours un son
       SYNTHÉTISÉ : il boucle sans couture, là où un one-shot enregistré
       « cliquerait » à chaque tour.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Tir primaire par arme. VULCAN garde le `player_shot` de base. */
export const WEAPON_SOUND: Readonly<Record<string, string>> = {
  vulcan: 'player_shot',
  plasma: 'shot_plasma',
  faisceau: 'shot_beam',
  traqueur: 'shot_homing',
  perforant: 'shot_pierce',
  ricochet: 'shot_bounce',
  eventail: 'shot_spread',
};

/** Déclenchement de la secondaire, regroupé par ARCHÉTYPE (salve, mine, rayon,
 *  éclair, déploiement, riposte) plutôt qu'un timbre par arme — six familles
 *  sonores lisibles valent mieux que dix nuances qu'on ne distingue pas. */
export const SECONDARY_SOUND: Readonly<Record<string, string>> = {
  'missiles-traqueurs': 'sec_missile',
  'lance-perforante': 'sec_missile',
  'trainee-de-salves': 'sec_missile',
  'mines-ascendantes': 'sec_mine',
  'rayon-continu': 'sec_beam',
  'faisceau-bref': 'sec_beam',
  'orage-electrique': 'sec_electric',
  'scies-orbitales': 'sec_deploy',
  'drones-flanqueurs': 'sec_deploy',
  'riposte-rapprochee': 'sec_riposte',
};

/** Boucle en vol, seulement pour les armes dont un projectile PERSISTE à
 *  l'écran et devrait « se faire entendre » tant qu'il vole. */
export const SECONDARY_LOOP: Readonly<Record<string, string>> = {
  // Missile unique qui file/traque → un souffle de réacteur tant qu'il vole.
  'missiles-traqueurs': 'missile_loop',
  'lance-perforante': 'missile_loop',
  // ⚠️ PAS `trainee-de-salves` : c'est une SALVE de tirs discrets (12 volées),
  //    son identité est la SUCCESSION des départs, pas un bourdon continu.
  'rayon-continu': 'beam_loop',
  'scies-orbitales': 'saw_loop',
};

/** DÉCHARGE d'ULTI par arme : chaque secondaire a sa déflagration propre (le
 *  faisceau orbital, la nuée de missiles, l'orage…). Repli sur `ulti_blast`. */
export const SECONDARY_ULTI: Readonly<Record<string, string>> = {
  'missiles-traqueurs': 'ulti_missile',
  'lance-perforante': 'ulti_missile',
  'trainee-de-salves': 'ulti_missile',
  'mines-ascendantes': 'ulti_mine',
  'rayon-continu': 'ulti_beam',
  'faisceau-bref': 'ulti_beam',
  'orage-electrique': 'ulti_electric',
  'scies-orbitales': 'ulti_deploy',
  'drones-flanqueurs': 'ulti_deploy',
  'riposte-rapprochee': 'ulti_riposte',
};

/** Son de tir d'une arme primaire (repli sur le générique). */
export function weaponSound(weaponId: string): string {
  return WEAPON_SOUND[weaponId] ?? 'player_shot';
}
